import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './components/admin/AuthProvider';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import Header from './components/Header';
import Hero from './components/Hero';
import ServiceSection from './components/ServiceSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import HeroEditor from './components/admin/sections/HeroEditor';
import ServicesEditor from './components/admin/sections/ServicesEditor';
import AboutEditor from './components/admin/sections/AboutEditor';
import ContactEditor from './components/admin/sections/ContactEditor';
import FooterEditor from './components/admin/sections/FooterEditor';
import SettingsEditor from './components/admin/sections/SettingsEditor';

// Main website component
const MainWebsite = () => {
  useEffect(() => {
    // Set Estonian language attributes
    document.documentElement.lang = 'et';
    document.documentElement.setAttribute('dir', 'ltr');
    
    // Add Estonian-specific meta tags dynamically
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=yes');
    }

    // Add Estonian timezone
    const now = new Date();
    const estonianTime = new Intl.DateTimeFormat('et-EE', {
      timeZone: 'Europe/Tallinn',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(now);
    
    console.log('Estonian time:', estonianTime);

    // Estonian keyboard support
    document.addEventListener('keydown', (e) => {
      // Support for Estonian characters
      if (e.altKey) {
        switch (e.key) {
          case 'a':
            e.preventDefault();
            break;
          case 'o':
            e.preventDefault();
            break;
          case 'u':
            e.preventDefault();
            break;
        }
      }
    });

    return () => {
      document.removeEventListener('keydown', () => {});
    };
  }, []);

  return (
    <div className="font-body text-gray-800 bg-gray-50 min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <Hero />
        <ServiceSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
      
      {/* Back to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-primary-700 hover:bg-primary-800 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-40"
        aria-label="Tagasi üles"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Main website */}
          <Route path="/" element={<MainWebsite />} />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/hero" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <HeroEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/services" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ServicesEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/about" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AboutEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/contact" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ContactEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/footer" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <FooterEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <SettingsEditor />
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/\" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;